import { Component, OnInit } from '@angular/core';
import { fontSize_opentab } from './contractor-head-count.config';
import { Priority } from '@constants/global.enum';
import { Router } from '@angular/router';
import { ContractorService } from './contractor-head-count.service';
import { AuthService } from '@core/auth.service';
import { ContractorHeadCountLabels } from '@constants/global.enum';

interface DonutSegment {
  color: string;
  dashArray: string;
  dashOffset: number;
}

const DONUT_RADIUS = 28;
const DONUT_CIRC = 2 * Math.PI * DONUT_RADIUS;

const HEADCOUNT_COLORS = {
  onBoarded: '#003F2D',
  offBoarded: '#dc2626',
};

@Component({
  selector: 'app-contractor-head-count',
  templateUrl: './contractor-head-count.component.html',
  styleUrl: './contractor-head-count.component.scss',
})
export class ContractorHeadCountComponent implements OnInit {
  month: Array<{ label: string; value: number; selected?: boolean }> = [];
  selectedMonth: number = 0;
  selectedYear: number = new Date().getFullYear();
  totalContractor: number = 0;
  contractorOnBoarded: number = 0;
  contractorOffBoarded: number = 0;
  priority = Priority;
  fontSize_opentab = fontSize_opentab;
  employeeId: string = '';
  selectedmonth: { label: string; value: string; selected?: boolean } | null = null;
  ContractorHeadCountLabels = ContractorHeadCountLabels;
  donutSegments: DonutSegment[] = [];
  headcountColors = HEADCOUNT_COLORS;

  constructor(
    private router: Router,
    private contractorService: ContractorService,
    private authService: AuthService,
  ) {}

  async ngOnInit() {
    this.generateLast12Months();
    const now = new Date();
    const currentMonthLabel = now.toLocaleString('default', { month: 'long', year: 'numeric' });
    const currentMonthOption = this.month.find((option) => option.label === currentMonthLabel);
    if (currentMonthOption) {
      this.selectedMonth = currentMonthOption.value;
    }
    // Fetch employeeId from AuthService
    const profile = await this.authService.getEmployeeDetails();
    if (profile && profile.employeeId) {
      this.employeeId = profile.employeeId;
      this.fetchContractorHeadCount(this.selectedMonth);
    }
  }

  generateLast12Months() {
    const now = new Date();
    for (let i = 0; i < 12; i++) {
      const monthDate = new Date(now.getFullYear(), now.getMonth() - i);
      const monthLabel = monthDate.toLocaleString('default', { month: 'long', year: 'numeric' });
      const monthValue = monthDate.getMonth() + 1;
      this.month.push({ label: monthLabel, value: monthValue, selected: i == 0 });
    }
  }

  fetchContractorHeadCount(month: number, year?: number) {
    const selectedYear = year ?? new Date().getFullYear();
    if (!this.employeeId) return;
    this.contractorService.getContractorsHeadCount(this.employeeId, month, selectedYear).subscribe({
        next: (response: any) => {
          if (response && response.data) {
            const data = response.data;
            this.totalContractor = data.totalContractors ?? 0;
            this.contractorOnBoarded = data.contractorsOnBoarded ?? 0;
            this.contractorOffBoarded = data.contractorsOffBoarded ?? 0;
          } else {
            this.resetHeadCount();
          }
          this.rebuildDonut();
        },
        error: () => {
          this.resetHeadCount();
          this.rebuildDonut();
        }
      });
  }

  private resetHeadCount() {
    this.totalContractor = 0;
    this.contractorOnBoarded = 0;
    this.contractorOffBoarded = 0;
  }

  private rebuildDonut(): void {
    const values = [this.contractorOnBoarded, this.contractorOffBoarded];
    const colors = [HEADCOUNT_COLORS.onBoarded, HEADCOUNT_COLORS.offBoarded];
    const breakdownTotal = values.reduce((sum, v) => sum + v, 0);
    if (breakdownTotal <= 0) {
      if (this.totalContractor > 0) {
        this.donutSegments = [{
          color: HEADCOUNT_COLORS.onBoarded,
          dashArray: `${DONUT_CIRC} 0`,
          dashOffset: 0,
        }];
      } else {
        this.donutSegments = [];
      }
      return;
    }
    const segments: DonutSegment[] = [];
    let acc = 0;
    values.forEach((value, idx) => {
      if (value <= 0) return;
      const arcLength = (value / breakdownTotal) * DONUT_CIRC;
      segments.push({
        color: colors[idx],
        dashArray: `${arcLength} ${DONUT_CIRC - arcLength}`,
        dashOffset: -acc,
      });
      acc += arcLength;
    });
    this.donutSegments = segments;
  }

  valueSelect(event: any) {
    let selectedValue: number | undefined;
    let selectedYear: number | undefined;
    if (typeof event === 'string') {
      const monthOption = this.month.find((option) => option.label === event);
      selectedValue = monthOption ? monthOption.value : undefined;
    } else if (event.value) {
      selectedValue = event.value;
    }
    if (selectedValue && this.month.some((option) => option.value === selectedValue)) {
      this.selectedMonth = Number(selectedValue);
      const currentDate = new Date();
      const currentMonth = currentDate.getMonth() + 1;
      const currentYear = currentDate.getFullYear();
      if (this.selectedMonth < currentMonth) {
        selectedYear = currentYear;
      } 
      else if (this.selectedMonth === currentMonth) {
        selectedYear = currentYear;
      }
      else {
        selectedYear = currentYear - 1;
      }

      this.selectedYear = selectedYear;
      this.fetchContractorHeadCount(this.selectedMonth, selectedYear);
    } else {
      this.resetHeadCount();

    }

  }

  navigateToContractorsList(): void {
    const selectedMonthValue = this.selectedMonth;
    const selectedYearValue = this.selectedYear;

    this.router.navigate(['/vendor-management/contractors-list'], {
      queryParams: { month: selectedMonthValue, year: selectedYearValue },
    });
  }
}
